import { configureStore } from '@reduxjs/toolkit'
import { combineReducers } from '@reduxjs/toolkit'
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  createMigrate
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import authReducer from './reducer/authSlice'
import errorReducer from './reducer/errorSlice'
import pageReducer from './reducer/pageSlice'
import assessmentReducer from './reducer/assessmentSlice'
import studentReducer from './reducer/studentSlice'
import themeReducer from './reducer/themeSlice'
import assignmentReducer from './reducer/assignmentSlice'
import { studentApi } from 'services/studentApi'

const rootReducer = combineReducers({
  authReducer,
  errorReducer,
  pageReducer,
  assessmentReducer,
  assignmentReducer,
  studentReducer,
  themeReducer,
  [studentApi.reducerPath]: studentApi.reducer
})

const persistConfig = {
  key: 'root',
  storage,
  version: 2,
  /**
   * Remove previously persisted transient UI state so interrupted requests
   * cannot keep loading or error states active after a page reload.
   */
  migrate: createMigrate(
    {
      2: (state: any) => {
        if (!state) {
          return state
        }

        const { pageReducer, errorReducer, ...persistedState } = state
        return persistedState
      }
    },
    { debug: false }
  ),
  whitelist: [
    'authReducer',
    'assignmentReducer',
    'assessmentReducer',
    'studentReducer',
    'themeReducer'
  ]
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export function makeStore() {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
        }
      }).concat(studentApi.middleware),
    devTools: true
  })
}

const store = makeStore()
export const persistor = persistStore(store)

export default store

export type AppState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch
